import { useEffect } from "react";
import { Flex, Typography, Image, App } from "antd";
import CopyIcon from "../../../assets/icons/copy.svg";
import { CheckOutlined } from "@ant-design/icons";
import VerifiedIcon from "../../../assets/icons/verified-icon.svg";
import Whatsapp from "../../../assets/icons/whatsapp.svg";
import Mail from "../../../assets/icons/mail.svg";
import { useDispatch, useSelector } from "react-redux";
import { getEscrowDetailsByOrgId, sendDepositDetails } from "../../../redux/slices/ProfileSlice";
import { capitalizeEntireWord } from "../../../helpers/helpers";
import Loader from "../../../components/CustomLoader/Loader";
import NoData from "../../../components/NoData/NoData";
import { DESC_TXT, GLOBAL_CONSTANTS, SUCCESS_MSGS, TITLE_TXT } from "../../../constants/constants";
import ProfileDetail from "../../../components/Details/ProfileDetail";
import ShareDetail from "../../../components/Details/ShareDetail";

const { Title, Text } = Typography;

const EscrowDetails = () => {
    const { message } = App.useApp();
    const { user } = useSelector((state) => state.auth);
    const { escrowDetailsData, loading } = useSelector((state) => state.profile);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(getEscrowDetailsByOrgId(user?.orgId));
    }, [dispatch, user.orgId]);

    const copyable = {
        icon: [
            <Image preview={false} src={CopyIcon} alt="Copy Icon" key="copy-icon" />,
            <CheckOutlined key="copy-icon" />,
        ],
    };

    const handleSendDeposit = async () => {
        const payload = {
            email: user?.email,
            name: user?.userName,
            beneficiaryName: escrowDetailsData?.accHolderName,
            escrowBankName: escrowDetailsData?.bankName,
            escrowBranchName: escrowDetailsData?.branchName,
            escrowAccountNumber: escrowDetailsData?.accNum,
            escrowIfscCode: escrowDetailsData?.ifscCode,
        };

        const res = await dispatch(sendDepositDetails(payload));

        if (res.payload.statusCode === 200) {
            message.success(SUCCESS_MSGS.ECW_DET_SUC_MSG);
        }
    };

    // Format all escrow details into one text block for copying
    const getFormattedEscrowDetails = () => {
        return `
Beneficiary Name: ${capitalizeEntireWord(escrowDetailsData?.accHolderName)}
Bank Account Number: ${escrowDetailsData?.accNum}
Bank Name: ${capitalizeEntireWord(escrowDetailsData?.bankName)}
Branch Name: ${capitalizeEntireWord(escrowDetailsData?.branchName)}
IFSC Code: ${escrowDetailsData?.ifscCode}
        `.trim();
    };

    // Handler to copy all details to clipboard
    const handleCopyAllDetails = () => {
        const text = getFormattedEscrowDetails();
        navigator.clipboard.writeText(text).then(() => {
            message.success("Virtual Account details copied successfully!");
        });
    };

    return (
        <>
            {loading ? (
                <Loader />
            ) : (
                <div className="investor-details-container">
                    <div className="header-container">
                        <Flex gap={10}>
                            <Title level={4}>{TITLE_TXT.ECW_DETS}</Title>
                            {escrowDetailsData?.bankAccId && <img src={VerifiedIcon} alt="Verified" />}
                        </Flex>
                    </div>
                    {escrowDetailsData?.bankAccId ? (
                        <div className="details-container">
                            <ProfileDetail
                                label={TITLE_TXT.ECW_BEN_ACC_NAME}
                                value={capitalizeEntireWord(escrowDetailsData?.accHolderName)}
                                copyable={copyable}
                            />

                            <ProfileDetail
                                label={TITLE_TXT.ECW_BANK_ACC_NUM}
                                value={escrowDetailsData?.accNum}
                                copyable={copyable}
                            />

                            <ProfileDetail
                                label={TITLE_TXT.ECW_BANK_NAME}
                                value={capitalizeEntireWord(escrowDetailsData?.bankName)}
                                copyable={copyable}
                            />

                            <ProfileDetail
                                label={TITLE_TXT.ECW_BRANCH_NAME}
                                value={capitalizeEntireWord(escrowDetailsData?.branchName)}
                                copyable={copyable}
                            />

                            <ProfileDetail
                                label={TITLE_TXT.ECW_IFSC_CODE}
                                value={escrowDetailsData?.ifscCode}
                                copyable={copyable}
                            />

                            <Text strong>{GLOBAL_CONSTANTS.SHARE_TXT}</Text>
                            <div className="share-container">
                                <ShareDetail
                                    popTitle={TITLE_TXT.DEPO_DET_TO_WHATSAPP_CONFIRM_TXT}
                                    onConfirm={() => { }}
                                    icon={Whatsapp}
                                    shareDesc={GLOBAL_CONSTANTS.WHATSAPP}
                                />
                                <ShareDetail
                                    popTitle={TITLE_TXT.DEPO_DET_TO_EMAIL_CONFIRM_TXT}
                                    onConfirm={handleSendDeposit}
                                    icon={Mail}
                                    shareDesc={GLOBAL_CONSTANTS.EMAIL}
                                />
                                <ShareDetail
                                    popTitle="Copy all escrow details?"
                                    onConfirm={handleCopyAllDetails}
                                    icon={CopyIcon}
                                    shareDesc="Copy All"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className='no-data-container'>
                            <NoData desc={DESC_TXT.ECW_DETS_NOT_AVAIL} />
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default EscrowDetails;
